import { DEFAULT_CUSTOM_EDGE_CONFIG, EDGE_ID_PREFIX, MIN_NODE_HEIGHT, MIN_VERTICAL_SPACING } from './constants'
import { RelativeDateDisplayMetric } from '@pushly/models/lib/enums/relative-date-display-metric'
import { EdgeChange, NodeChange } from '@xyflow/react'
import { getRandId } from '@pushly/models/lib/utils/get-rand-id'

import {
    ActionStep,
    AddStepType,
    DelayStep,
    EditableNode,
    ExitStep,
    GlobalTriggerStep,
    isActionJourneyNode,
    isSendRecommendedNotificationActionStep,
    JourneyEdge,
    JourneyNode,
    JourneyNodeDataType,
    JourneyStep,
    JourneyStepData,
    SendNotificationActionStep,
    SendRecommendedNotificationActionStep,
    TriggerStep,
} from './types/journey-nodes'
import { CopyTypes, JourneyState } from './types/journey-context'
import { JourneyActionType } from '@pushly/models/lib/enums/journeys/journey-action-type'
import { RecommendationSource } from '@pushly/models/lib/enums/recommendations/recommendation-source'
import { BlueConicRecommendedNotificationParams } from '@pushly/models/lib/structs/journeys'
import { DomainIntegrationProviders } from '@pushly/models/lib/enums/domain-integration-providers'
import { GetBlueConicIntegrationResponse } from '@pushly/models/lib/structs/domains'
import { Journey } from './types/journey-api-response'
import { DeliveryChannel } from '@pushly/models/lib/enums'
import { DelayType, StepType } from '@pushly/models/lib/enums/journeys'
import { CatalogItemPickStrategy } from '@pushly/models/lib/enums/catalogs'
import { safeEnumFromValue } from '@pushly/models'

// region Basic Utils

export const getEdgeId = (id: number | string) => `${EDGE_ID_PREFIX}${id}`

export const handleNodeParamsUpdate = <T extends EditableNode>(
    node: T,
    params: Partial<T['data']['step']['configuration']['params']>,
): T => {
    node.data.step.configuration.params = {
        ...node.data.step.configuration.params,
        ...params,
    } as typeof node.data.step.configuration.params // @todo - remove once journey notification types are fixed
    return node
}

/**
 * Transpose nodes from ReactFlow journey back into steps API schema
 *
 * @param state
 */
export const parseJourneyFromFlow = (state: JourneyState) => {
    const steps: any[] = []
    const { nodes, journey } = state
    if (!journey) {
        return
    }
    const journeyUpdate = structuredClone(journey)

    for (const node of nodes) {
        const nodeData = node.data.step
        // API CREATE/PUT step
        const step: JourneyStep = {
            ...nodeData, // 'abc' if new | '123' if already present
            campaign_id: !journey ? undefined : journey.id,
            configuration: {
                ...nodeData.configuration,
                out_step_id: nodeData.configuration.out_step_id, // parseInt(node.data.configuration.out_step_id, 10) || node.data.configuration.out_step_id,
            },
        }

        steps.push(step)
    }

    journeyUpdate.steps = steps

    return journeyUpdate
}

function getBaseConfigurationForStepType(
    nodeType:
        | StepType.DELAY
        | JourneyActionType.SEND_NOTIFICATION
        | JourneyActionType.SEND_CART_NOTIFICATION
        | JourneyActionType.SEND_VIEWED_ITEM_NOTIFICATION
        | JourneyActionType.SEND_SAVED_ITEM_NOTIFICATION
        | JourneyActionType.SEND_RECOMMENDED_NOTIFICATION,
    stepId: string,
    outStepId: number | string,
    addtOptions?: {
        overrideData?: ActionStep | DelayStep
        overrideType?: CopyTypes
        meta?: Record<string, any>
        channels?: DeliveryChannel[]
    },
): DelayStep | ActionStep {
    const actionIdx = addtOptions?.meta?.actionIndex

    switch (nodeType) {
        case StepType.DELAY: {
            const override = addtOptions?.overrideData as DelayStep | undefined
            if (override) {
                return {
                    ...override,
                    id: stepId,
                    configuration: {
                        ...override.configuration,
                        out_step_id: outStepId,
                    },
                }
            }
            return {
                id: stepId,
                type: StepType.DELAY,
                configuration: {
                    type: DelayType.RELATIVE,
                    params: {
                        delay_seconds: 60 * 60 * 24 * 2, // 2 days
                        qualifier: RelativeDateDisplayMetric.DAYS,
                    },
                    out_step_id: outStepId,
                },
                // propagate meta supplied by caller (e.g., actionIndex)
                meta: { ...(addtOptions?.meta ?? {}) },
            }
        }

        case JourneyActionType.SEND_NOTIFICATION: {
            const override = addtOptions?.overrideData as SendNotificationActionStep | undefined
            let overrideParams = override?.configuration?.params
            if (override && addtOptions?.overrideType === CopyTypes.COPY) {
                const { notification_id, ...notification } = override.configuration.params ?? {}
                overrideParams = notification
            }

            if (override) {
                return {
                    ...override,
                    id: stepId,
                    configuration: {
                        type: nodeType,
                        params: overrideParams ?? {},
                        out_step_id: outStepId,
                    },
                    meta: { ...(override.meta ?? {}), ...(addtOptions?.meta ?? {}) },
                }
            }

            return {
                id: stepId,
                type: StepType.ACTION,
                configuration: {
                    type: nodeType,
                    params: {},
                    out_step_id: outStepId,
                },
                meta: { ...(addtOptions?.meta ?? {}) },
            }
        }

        case JourneyActionType.SEND_CART_NOTIFICATION:
        case JourneyActionType.SEND_VIEWED_ITEM_NOTIFICATION:
        case JourneyActionType.SEND_SAVED_ITEM_NOTIFICATION: {
            const override = addtOptions?.overrideData as ActionStep | undefined
            let overrideParams = (override as any)?.configuration?.params
            if (override && addtOptions?.overrideType === CopyTypes.COPY) {
                const { notification_id, ...notification } = (override as any)?.configuration?.params ?? {}
                overrideParams = notification
            }

            if (override) {
                return {
                    ...(override as ActionStep),
                    id: stepId,
                    configuration: {
                        type: nodeType,
                        params: {
                            ...(overrideParams ?? {}),
                        },
                        out_step_id: outStepId,
                    },
                    meta: { ...((override as ActionStep).meta ?? {}), ...(addtOptions?.meta ?? {}) },
                }
            }

            // New step defaults: seed item_strategy based on index
            const defaultStrategy =
                !!actionIdx && actionIdx > 0
                    ? CatalogItemPickStrategy.PREVIOUSLY_SELECTED
                    : CatalogItemPickStrategy.MOST_RECENT

            return {
                id: stepId,
                type: StepType.ACTION,
                configuration: {
                    type: nodeType,
                    params: {
                        item_strategy: defaultStrategy,
                    } as any,
                    out_step_id: outStepId,
                },
                meta: { ...(addtOptions?.meta ?? {}) },
            }
        }

        case JourneyActionType.SEND_RECOMMENDED_NOTIFICATION: {
            const override = addtOptions?.overrideData as SendRecommendedNotificationActionStep | undefined

            if (override) {
                return {
                    ...override,
                    id: stepId,
                    configuration: {
                        ...override.configuration,
                        out_step_id: outStepId,
                    },
                    meta: { ...(override.meta ?? {}), ...(addtOptions?.meta ?? {}) },
                }
            }

            return {
                id: stepId,
                type: StepType.ACTION,
                configuration: {
                    type: nodeType,
                    params: new BlueConicRecommendedNotificationParams(),
                    out_step_id: outStepId,
                },
                meta: { ...(addtOptions?.meta ?? {}) },
            }
        }

        default: {
            return {
                id: stepId,
                type: StepType.ACTION,
                configuration: {
                    type: nodeType as any,
                    params: {},
                    out_step_id: outStepId,
                },
                meta: { ...(addtOptions?.meta ?? {}) },
            }
        }
    }
}

// endregion //

// region Node Manipulation

/**
 * Takes journey steps and orders them head to tail via id -> out_step_id -> next.id
 *
 * @param steps
 */
export const orderJourneySteps = (steps: Journey['steps']) => {
    const orderedSteps: Journey['steps'] = []

    let nextStep = steps.find((step) => step.type === StepType.TRIGGER)
    while (nextStep) {
        orderedSteps.push(nextStep)

        if (!nextStep.configuration.out_step_id) {
            break
        }
        nextStep = steps.find((step) => step.id === nextStep!.configuration.out_step_id)
    }

    return orderedSteps
}

/**
 * Transpose Journey steps into nodes and edges to be passed to a ReactFlow.
 *
 * @param steps - can be unordered as ordering is conducted during logic
 * @param dependencies - used to determine if integration is valid for a given step
 */
export const buildInitialJourneyFlow = (
    steps: Journey['steps'],
    dependencies: JourneyState['dependencies'],
): [nodes: JourneyNode[], edges: JourneyEdge[]] => {
    const nodeUpdates: JourneyNode[] = []
    const edgeUpdates: JourneyEdge[] = []

    // metadata for send_cart notification UI
    let actionIndex = 0
    orderJourneySteps(steps).forEach((step, idx) => {
        if (!step.type) return

        const data = new JourneyStepData<
            JourneyNodeDataType<ActionStep | DelayStep | GlobalTriggerStep | TriggerStep | ExitStep>
        >({
            step: {
                id: step.id,
                type: step.type,
                configuration: step.configuration,
                meta: step.meta,
            },
            uiMeta: {},
        })

        const newNode: JourneyNode = {
            id: step.id.toString(),
            position: {
                x: 0,
                y: idx * 120,
            },
            data,
            type: 'custom',
        }

        // global trigger is positioned hidden before the rest of the graph
        if (safeEnumFromValue(StepType, data.step.type) === StepType.GLOBAL_TRIGGER) {
            newNode.hidden = true
            newNode.position = {
                x: 0,
                y: -120,
            }
        }

        // action editor uses the position of the action in the flow to show specific options
        // set actionIndex according to order of actions from steps
        if (isActionJourneyNode(newNode)) {
            newNode.data.step.meta = {
                ...data.step.meta,
                actionIndex,
            }

            newNode.data.resetInitialState({ step: newNode.data.step, uiMeta: newNode.data.uiMeta })

            actionIndex++
        }

        if (
            dependencies.integrations &&
            isActionJourneyNode(newNode) &&
            isSendRecommendedNotificationActionStep(newNode.data.step)
        ) {
            const recommendationSource = newNode.data.step.configuration.params.recommendation_source

            if (recommendationSource === RecommendationSource.BLUECONIC) {
                let integrationDisabled = false
                let storeRemoved = false

                const stepIntegration = newNode.data.step.configuration.params.integration
                const integrationMatch = dependencies.integrations.get(stepIntegration.integration_id)

                if (integrationMatch) {
                    const storeMatch = (integrationMatch?.data as GetBlueConicIntegrationResponse)?.stores[
                        stepIntegration.store_id
                    ]

                    if (!storeMatch) {
                        storeRemoved = true
                    }
                } else {
                    integrationDisabled = true
                }

                if (integrationDisabled || storeRemoved) {
                    const options = ['remove']
                    let reason
                    if (integrationDisabled) {
                        reason = 'integration being removed, disabled or incorrectly configured.'

                        if (
                            Array.from(dependencies.integrations).some(
                                ([_, int]) => int.provider === DomainIntegrationProviders.BLUECONIC_REC,
                            )
                        ) {
                            // when other blueconic integrations / stores are present, user can update this step to use those data sources
                            options.push('edit')
                        }
                    } else if (storeRemoved) {
                        reason = 'store being removed.'
                        // when other stores are present, user can update this step to use those data sources
                        options.push('edit')
                    }

                    data.setUIMeta({
                        invalid: {
                            options,
                            reason:
                                'This step is no longer sending notifications due to the ' +
                                reason +
                                ' Users in the Journey will be moved to the next step.',
                        },
                    })
                }
            }
        }

        nodeUpdates.push(newNode)

        // edges join node to node via source, target properties
        if (step.configuration.out_step_id) {
            const newEdge: JourneyEdge = {
                ...DEFAULT_CUSTOM_EDGE_CONFIG,
                id: getEdgeId(step.id),
                source: step.id.toString(),
                target: step.configuration.out_step_id.toString(),
            }

            edgeUpdates.push(newEdge)
        }
    })

    return [nodeUpdates, edgeUpdates]
}

export const getAddNodeMutations = (
    nodeType: Exclude<AddStepType, 'PASTE_COPIED_NODE'>,
    nodes: JourneyNode[],
    sourceNode: JourneyNode,
    targetNode: JourneyNode,
    overrideData?: ActionStep | DelayStep,
    overrideType?: CopyTypes,
): [
    nodeChanges: NodeChange<JourneyNode>[],
    edgeChanges: EdgeChange<JourneyEdge>[],
    newNode: JourneyNode | undefined,
] => {
    const nodeChanges: NodeChange<JourneyNode>[] = []
    const edgeChanges: EdgeChange<JourneyEdge>[] = []

    const sourceNodeId = sourceNode.data.id
    const targetNodeId = targetNode.data.id

    const newNodeId = getRandId()

    // adding a step after exit not supported
    if (sourceNode.data.step.type === StepType.EXIT) {
        return [nodeChanges, edgeChanges, undefined]
    }

    // re-assign out_step_id to new node - added to changes via below type: "replace" change
    sourceNode.data.step.configuration.out_step_id = newNodeId
    const meta: Record<string, any> = {}

    // if an ACTION step is added determine it's step index
    let newActionIndex
    if (
        nodeType === JourneyActionType.SEND_NOTIFICATION ||
        nodeType === JourneyActionType.SEND_CART_NOTIFICATION ||
        nodeType === JourneyActionType.SEND_VIEWED_ITEM_NOTIFICATION ||
        nodeType === JourneyActionType.SEND_SAVED_ITEM_NOTIFICATION
    ) {
        const startingIndex = nodes.findIndex((n) => n.data.step.id === sourceNodeId)

        if (startingIndex > 0) {
            // Scan backwards from the source to find the most recent ACTION and set index + 1
            for (let i = startingIndex; i >= 0; i--) {
                if (nodes[i].data.step.type === StepType.ACTION) {
                    const prevIdx = nodes[i].data.step.meta?.actionIndex ?? -1
                    newActionIndex = prevIdx + 1
                    break
                }
            }
        } else {
            newActionIndex = 0
        }

        meta.actionIndex = newActionIndex
    }

    const data = new JourneyStepData({
        step: getBaseConfigurationForStepType(nodeType, newNodeId, targetNodeId, { overrideData, overrideType, meta }),
        uiMeta: { source: overrideType ? overrideType : 'add' },
    })

    // Ensure the computed actionIndex is actually set on the new Action step
    if (
        nodeType === JourneyActionType.SEND_NOTIFICATION ||
        nodeType === JourneyActionType.SEND_CART_NOTIFICATION ||
        nodeType === JourneyActionType.SEND_VIEWED_ITEM_NOTIFICATION ||
        nodeType === JourneyActionType.SEND_SAVED_ITEM_NOTIFICATION
    ) {
        data.step.meta = {
            ...(data.step.meta ?? {}),
            actionIndex: newActionIndex ?? 0,
        }
    }

    const newNode: JourneyNode = {
        id: newNodeId,
        data,
        position: {
            x: 0,
            y: 0, // auto layout called in base builder should layout position
        },
        type: 'custom',
    }

    // update nodes from source to new and new to target
    // current: source { out_step_id: target }
    // new: source {out_step_id: new }, new {out_step_id: target}
    nodeChanges.push(
        // mutation to add newly created step/node
        {
            type: 'add',
            item: newNode,
        },
        // replace source node with out_step_id: newNodeId
        {
            type: 'replace',
            id: sourceNodeId.toString(),
            item: sourceNode,
        },
    )

    /**
     * update edges
     * ```
     *  _________________________________________________________
     * |                                                        |
     * |    current                                             |
     * |        nodes: [source] -----> [target]                 |
     * |        edges:      \__eid-source__/                    |
     * |--------------------------------------------------------|
     * |                                                        |
     * |    new                                                 |
     * |        nodes: [source]----> [new node] ----> [target]  |
     * |        edges:     \_eid-source_/ \_eid-new-node_/      |
     * |________________________________________________________|
     *
     * ```
     */
    edgeChanges.push(
        // add edge from source->new node
        {
            type: 'add',
            item: {
                ...DEFAULT_CUSTOM_EDGE_CONFIG,
                id: getEdgeId(sourceNodeId),
                source: sourceNodeId.toString(),
                target: newNodeId.toString(),
            },
        },
        // add edge from new node->target
        {
            type: 'add',
            item: {
                ...DEFAULT_CUSTOM_EDGE_CONFIG,
                id: getEdgeId(newNodeId),
                source: newNodeId,
                target: targetNodeId.toString(),
            },
        },
        // remove old edge from source->target
        {
            type: 'remove',
            id: getEdgeId(sourceNodeId),
        },
    )

    // apply position/meta changes for each node occurring after the added node
    nodeChanges.push(...applyChangesPostNode('add', nodes, targetNodeId.toString(), newNode.data.step.type))

    return [nodeChanges, edgeChanges, newNode]
}

export const getRemoveNodeMutations = (
    node: JourneyNode,
    nodes: JourneyNode[],
    edges: JourneyEdge[],
): [nodeChanges: NodeChange<JourneyNode>[], edgeChanges: EdgeChange<JourneyEdge>[]] => {
    const nodeChanges: NodeChange<JourneyNode>[] = []
    const edgeChanges: EdgeChange<JourneyEdge>[] = []

    // cannot remove Trigger or Exit steps return empty changes
    if (node.data.step.type === StepType.EXIT || node.data.step.type === StepType.TRIGGER) {
        return [nodeChanges, edgeChanges]
    }

    const sourceNodeEdge = edges.find((e) => e.target === node.id)!
    const targetNodeEdge = edges.find((e) => e.source === node.id)!

    const prevNodeIdx = nodes.findIndex((n) => n.id === sourceNodeEdge.source)!
    const prevNode = nodes[prevNodeIdx]
    const postNodeIdx = nodes.findIndex((n) => n.id === targetNodeEdge.target)
    const postNode = nodes[postNodeIdx]

    prevNode.data.step.configuration.out_step_id = postNode.data.id

    /**
     * update node/edges
     * ```
     *  _________________________________________________________
     * |                                                        |  [action] node will be removed
     * |    current                                             |
     * |        nodes: [trigger]----> [action]  ---->  [exit]   |  [trigger] will be replaced with
     * |        edges:     \_eid-trigger_/ \_eid-action_/       |       [trigger] out_step_id: exit.id
     * |--------------------------------------------------------|
     * |                                                        |  eid-action edge will be removed
     * |    new                                                 |
     * |        nodes: [trigger]  ----->  [exit]                |  eid-trigger will be replaced with
     * |        edges:      \__eid-trigger__/                   |       eid-trigger where target: exit.id
     * |________________________________________________________|
     *
     * ```
     */
    nodeChanges.push(
        {
            id: prevNode.id,
            type: 'replace',
            item: prevNode,
        },
        {
            id: node.id,
            type: 'remove',
        },
    )

    edgeChanges.push(
        {
            id: sourceNodeEdge.id,
            type: 'replace',
            item: {
                ...sourceNodeEdge,
                source: prevNode.id,
                target: postNode.id,
            },
        },
        {
            id: targetNodeEdge.id,
            type: 'remove',
        },
    )

    // apply position changes for each node occurring after the removed node
    nodeChanges.push(...applyChangesPostNode('remove', nodes, postNode.id, node.data.step.type))

    return [nodeChanges, edgeChanges]
}

/**
 * Traverses each step -> out_step_id chain and repositions and applies changes for each node from the affected starting
 * node.
 *
 * @param effect
 * @param nodes
 * @param startingId
 * @param nodeTypeChange
 */
export const applyChangesPostNode = (
    effect: 'add' | 'remove',
    nodes: JourneyNode[],
    startingId: string,
    nodeTypeChange?: StepType,
) => {
    const nodeChanges: NodeChange<JourneyNode>[] = []

    let nextStep: string | undefined = startingId
    while (nextStep) {
        const step = nodes.find((n) => n.id === nextStep)
        if (step) {
            if (step.data.step.type === StepType.ACTION && nodeTypeChange === StepType.ACTION) {
                step.data.step.meta.actionIndex =
                    effect === 'add' ? step.data.step.meta.actionIndex + 1 : step.data.step.meta.actionIndex - 1

                nodeChanges.push({
                    id: step.id,
                    type: 'replace',
                    item: {
                        ...step,
                        data: step.data,
                    },
                })
            }

            if (step.data.step.configuration.out_step_id) {
                nextStep = step.data.step.configuration.out_step_id.toString()
                continue
            }
        }

        nextStep = undefined
    }

    return nodeChanges
}

export const positionNodes = (nodes: JourneyNode[]): JourneyNode[] => {
    // Start coordinates for the first node
    let currentY = 0

    const triggerNode = nodes.find((n) => n.data.step.type === StepType.TRIGGER)
    if (!triggerNode) {
        return []
    }

    const updateNodes: JourneyNode[] = [
        {
            ...triggerNode,
            position: {
                x: 0,
                y: currentY,
            },
        },
    ]

    // Move to the next position (accounting for a 60px gap)
    currentY += (triggerNode.height ?? triggerNode.measured?.height ?? MIN_NODE_HEIGHT) + MIN_VERTICAL_SPACING

    let nextStepId: string | undefined = triggerNode.data.step.configuration.out_step_id?.toString()
    while (nextStepId) {
        const step = nodes.find((n) => n.id === nextStepId)
        if (step) {
            // Update node position
            const updatedNode = {
                ...step,
                position: {
                    x: 0, // Keep all steps aligned horizontally
                    y: currentY,
                },
            }

            // Move to the next position (accounting for a 60px gap)
            currentY += (step.height ?? step.measured?.height ?? MIN_NODE_HEIGHT) + MIN_VERTICAL_SPACING

            updateNodes.push(updatedNode)

            if (step.data.step.configuration.out_step_id) {
                nextStepId = step.data.step.configuration.out_step_id.toString()
                continue
            }
        }

        nextStepId = undefined
    }

    return updateNodes
}

// endregion
